import React, { useState, useRef, useEffect } from 'react';
import { Layout, SplitSquareHorizontal, Menu, X } from 'lucide-react';
import { Download } from 'lucide-react';
import html2canvas from 'html2canvas';
import PrefectureShape from './PrefectureShape';
import { prefectureData, experienceLevels } from './prefectureData';
import LanguageSelector from './LanguageSelector';
import { translations } from './config/languageConfig';
import ShareMenu from './ShareMenu';

const JapanMap = ({ currentLanguage="en", setCurrentLanguage }) => {
  const [prefectureLevels, setPrefectureLevels] = useState({});
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [isCompactLayout, setIsCompactLayout] = useState(false);
  const [isMobileLegendOpen, setIsMobileLegendOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const contentRef = useRef(null);
  const t = translations[currentLanguage];
  const [isMapDropdownOpen, setIsMapDropdownOpen] = useState(false);

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;

  const handleLevelSelect = (prefId, level) => {
    setPrefectureLevels(prev => ({
      ...prev,
      [prefId]: level
    }));
  };

  const getLevelColor = (prefId) => {
    const level = prefectureLevels[prefId];
    if (level === undefined) return 'white';
    const levelObj = experienceLevels.find(l => l.level === level);
    return levelObj?.fillColor || 'white';
  };

  const calculateTotalLevel = () => {
    return Object.values(prefectureLevels).reduce((acc, curr) => acc + curr, 0);
  };

  const renderLegendPanel = () => (
    <div className="space-y-3">
      {/* Stats Card */}
      <div className="bg-white/90 backdrop-blur rounded-xl shadow-sm border border-slate-200 p-3">
        <div className="space-y-2">
          <div>
            <div className="text-sm font-medium text-slate-500">{t.prefecturesVisited}</div>
            <div className="text-2xl font-bold text-slate-900 mt-0.5">
              {Object.keys(prefectureLevels).length}/47
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-slate-500">{t.totalScore}</div>
            <div className="text-2xl font-bold text-blue-600 mt-0.5">
              {calculateTotalLevel()}
            </div>
          </div>
        </div>
      </div>

      {/* Experience Levels */}
      <div className="bg-white/90 backdrop-blur rounded-xl shadow-sm border border-slate-200 p-3">
        <h2 className="text-sm font-semibold text-slate-800 mb-2">{t.experienceLevels}</h2>
        <div className="flex flex-col space-y-1.5">
          {experienceLevels.map((level) => (
            <button
              key={level.level}
              onClick={() => {
                setSelectedLevel(level.level);
                if (isMobile) {
                  setIsMobileLegendOpen(false);
                }
              }}
              className={`flex items-center space-x-2 p-1.5 rounded-lg transition-all duration-200
                ${selectedLevel === level.level
                ? 'bg-slate-50 ring-2 ring-offset-2 ' + level.ringColor
                : 'hover:bg-slate-50'}`}
            >
              <div className={`w-4 h-4 ${level.color} rounded-lg shadow-sm border border-slate-200`}/>
              <div className="text-left flex-1">
                <div className={`text-sm font-medium ${level.textColor}`}>
                  {t.experiences[level.level]}
                </div>
                <div className="text-xs text-slate-500">{t.level} {level.level}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const handleExport = async () => {
    try {
      if (!contentRef.current) return;
      const wrapper = document.createElement('div');
      wrapper.style.padding = '32px';
      wrapper.style.backgroundColor = '#f8fafc';
      wrapper.appendChild(contentRef.current.cloneNode(true));
      document.body.appendChild(wrapper);

      const canvas = await html2canvas(wrapper, {
        backgroundColor: '#f8fafc',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
      });

      document.body.removeChild(wrapper);
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `japan-travel-map-${new Date().toISOString().slice(0, 10)}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting map:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4 md:mb-6">
          <div className="flex items-center justify-between w-full md:w-auto">
            <div className="text-2xl md:text-4xl font-bold text-slate-800">JapanEx</div>
            {isMobile && (
              <button
                onClick={() => setIsMobileLegendOpen(!isMobileLegendOpen)}
                className="md:hidden p-2 text-slate-700 hover:text-slate-900"
              >
                {isMobileLegendOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 md:gap-4 relative">
            {/* New Dropdown Menu for Map Selection */}
            <div className="relative inline-block text-left">
              <div>
                <button
                  type="button"
                  className="inline-flex justify-between px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors rounded-lg"
                  onClick={() => setIsMapDropdownOpen(!isMapDropdownOpen)}
                >
                  {t.selectMap}
                  <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              {isMapDropdownOpen && (
                <div className="absolute right-0 z-10 mt-2 w-32 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                  <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                    <button
                      onClick={() => {
                        window.location.href = `/${currentLanguage}`;
                        setIsMapDropdownOpen(false);
                      }}
                      className="block w-full text-left px-2 py-2 text-sm text-slate-700 hover:bg-slate-100"
                    >
                      {t.japanMap}
                    </button>
                    <button
                      onClick={() => {
                        window.location.href = `/china/${currentLanguage}`;
                        setIsMapDropdownOpen(false);
                      }}
                      className="block w-full text-left px-2 py-2 text-sm text-slate-700 hover:bg-slate-100"
                    >
                      {t.chinaMap}
                    </button>
                  </div>
                </div>
              )}
            </div>
            {/* Existing Layout Button */}
            {!isMobile && (
              <button
                onClick={() => setIsCompactLayout(!isCompactLayout)}
                className="inline-flex items-center px-3 md:px-4 py-2 text-sm md:text-base font-medium text-slate-700 hover:text-slate-900 transition-colors rounded-lg hover:bg-slate-100"
              >
                {isCompactLayout ?
                  <SplitSquareHorizontal className="w-4 h-4 mr-2" /> :
                  <Layout className="w-4 h-4 mr-2" />
                }
                {t.layout}
              </button>
            )}
            <LanguageSelector
              currentLanguage={currentLanguage}
              onLanguageChange={setCurrentLanguage}
            />
            <button
              onClick={handleExport}
              className="inline-flex items-center px-3 md:px-4 py-2 text-sm md:text-base font-medium text-slate-700 hover:text-slate-900 transition-colors"
            >
              <Download className="w-4 h-4 mr-2"/>
              {t.export}
            </button>
            <ShareMenu contentRef={contentRef} t={t}/>
          </div>
        </div>

        {/* Main Content */}
        <div className={`${!isMobile && !isCompactLayout ? 'flex gap-6' : ''} relative`} ref={contentRef}>
          {/* Mobile Legend Panel */}
          {isMobile && isMobileLegendOpen && (
            <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40">
              <div className="absolute right-0 top-0 h-full w-64 bg-white p-3 overflow-y-auto">
                {renderLegendPanel()}
              </div>
            </div>
          )}

          {/* Desktop Legend Panel */}
          {!isMobile && !isCompactLayout && (
            <div className="w-64">
              {renderLegendPanel()}
            </div>
          )}

          {/* Map Container */}
          <div className="flex-1 bg-white rounded-2xl shadow-sm border border-slate-200 p-4 md:p-6">
            <div className="relative w-full">
              {!isMobile && isCompactLayout && (
                <div className="absolute left-4 top-4 w-80 z-10">
                  {renderLegendPanel()}
                </div>
              )}
              <div className="w-full" style={{
                aspectRatio: isMobile ? '3/4' : '4/3'
              }}>
                <svg
                  viewBox="300 -300 1200 1100"
                  className="w-full h-full"
                  style={{backgroundColor: '#EFF6FF'}}
                  preserveAspectRatio="xMidYMid meet"
                >
                  {prefectureData.map(prefecture => (
                    <PrefectureShape
                      key={prefecture.id}
                      {...prefecture}
                      fill={getLevelColor(prefecture.id)}
                      onSelect={handleLevelSelect}
                      currentLanguage={currentLanguage}
                    />
                  ))}
                  {/* Add watermark text */}
                  <text
                    x="1400"
                    y="750"
                    className="text-base fill-slate-400"
                    style={{
                      fontSize: '24px',
                      fontWeight: '500',
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}
                  >
                    world-ex.org
                  </text>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JapanMap;